import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  Typography,
  message,
  Badge,
  Input,
  Button,
  Space,
  DatePicker,
  Spin,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CalendarOutlined,
  DownOutlined,
  UnorderedListOutlined,
  BarChartOutlined,
  TableOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import Heading from "../../../components/Heading";
import urls from "../../../api/urls";
import API from "../../../api";
import "./style.scss";

const { Text } = Typography;

const DAProductivityReport = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [apiColumns, setApiColumns] = useState([]);
  const [tableConfig, setTableConfig] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
  });

  const [columnFilters, setColumnFilters] = useState({});
  const [reportDate, setReportDate] = useState(moment());
  const [viewType, setViewType] = useState("detailed"); // "detailed" or "count"
  const apiCallInProgress = useRef(false);

  const fetchReportData = async (
    page = 1,
    pageSize = 20,
    currentFilters = columnFilters,
    type = viewType,
    date = reportDate,
  ) => {
    if (apiCallInProgress.current) return;
    if (!date) return;

    setLoading(true);
    apiCallInProgress.current = true;

    try {
      const queryParams = {
        page: page,
        limit: pageSize,
        date: date.format("YYYY-MM-DD"),
      };

      // Add column filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && String(value).trim() !== "") {
          queryParams[key] = String(value).trim();
        }
      });

      const queryString = new URLSearchParams(queryParams).toString();
      const apiUrl =
        type === "detailed"
          ? urls.DA_PRODUCTIVITY_REPORT_DETAILED
          : urls.DA_PRODUCTIVITY_REPORT_COUNT;

      const response = await API.Api(
        apiUrl + "?" + queryString,
      ).getCustomHeader({
        kid: "1",
        authtype: "jwt",
      });

      if (response?.response?.data?.status === "success") {
        const resData = response.response.data;
        setData(resData.data || []);

        // Update pagination from meta if available
        if (resData.pagination) {
          setPagination({
            current: page,
            pageSize: pageSize,
            total: resData.pagination.totalCount || 0,
            showSizeChanger: true,
            pageSizeOptions: resData.pagination.pageSizeOptions?.map(
              String,
            ) || ["20", "50", "100", "200"],
          });
        }

        // Set table config if available
        if (resData.tableConfig) {
          setTableConfig(resData.tableConfig);
        }

        // Store columns from response
        if (resData.columns) {
          setApiColumns(resData.columns);
        }
      } else {
        message.error(
          response?.response?.data?.message || "Failed to fetch report data",
        );
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      message.error("Error fetching report data");
    } finally {
      setLoading(false);
      apiCallInProgress.current = false;
    }
  };

  const getColumnSearchProps = (col) => {
    const dataIndex = col.key;
    const title = col.title;

    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Search ${title}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              handleSearch(dataIndex, selectedKeys[0]);
              confirm();
            }}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(dataIndex, selectedKeys[0]);
                confirm();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                handleSearch(dataIndex, "");
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      filteredValue: columnFilters[dataIndex]
        ? [columnFilters[dataIndex]]
        : null,
    };
  };

  const handleSearch = (field, value) => {
    const newFilters = { ...columnFilters, [field]: value };
    setColumnFilters(newFilters);
    fetchReportData(1, pagination.pageSize, newFilters, viewType, reportDate);
  };

  const columns = useMemo(() => {
    return apiColumns.map((col) => {
      const formattedCol = {
        title: col.title,
        dataIndex: col.key,
        key: col.key,
        width: col.width || 150,
        sorter: col.sortable,
        ellipsis: true,
      };

      // Add filter props if enabled
      if (col.filter && col.filter.type === "search") {
        Object.assign(formattedCol, getColumnSearchProps(col));
      }

      // Handle custom rendering
      if (col.render) {
        if (col.render.type === "datetime") {
          formattedCol.render = (text) =>
            text
              ? moment(text).format(col.render.format || "DD-MM-YYYY HH:mm:ss")
              : "-";
        } else if (col.key === "shipmentStatus" || col.key === "status") {
          formattedCol.render = (status) => (
            <Badge
              status={
                status?.toLowerCase() === "delivered" ? "success" : "processing"
              }
              text={status}
            />
          );
        } else if (col.key === "awbNumber") {
          formattedCol.render = (text) => <Text strong>{text}</Text>;
        }
      }

      return formattedCol;
    });
  }, [apiColumns, columnFilters]);

  const handleTableChange = (pag, filters) => {
    const formattedFilters = { ...columnFilters };
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        [formattedFilters[key]] = filters[key];
      } else {
        delete formattedFilters[key];
      }
    });

    setColumnFilters(formattedFilters);
    fetchReportData(
      pag.current,
      pag.pageSize,
      formattedFilters,
      viewType,
      reportDate,
    );
  };

  const handleExport = async () => {
    if (!reportDate) return;

    setLoading(true);
    try {
      const queryParams = {
        export: true,
        date: reportDate.format("YYYY-MM-DD"),
      };

      // Add active column filters to export as well
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value && String(value).trim() !== "") {
          queryParams[key] = String(value).trim();
        }
      });

      const queryString = new URLSearchParams(queryParams).toString();

      const userData =
        JSON.parse(localStorage.getItem("userinfo")) ||
        JSON.parse(sessionStorage.getItem("userinfo"));

      const apiUrl =
        viewType === "detailed"
          ? urls.DA_PRODUCTIVITY_REPORT_DETAILED
          : urls.DA_PRODUCTIVITY_REPORT_COUNT;

      const axiosResponse = await axios.get(apiUrl + "?" + queryString, {
        responseType: "arraybuffer",
        headers: {
          Authorization: "Bearer " + userData?.accessToken,
          kid: "1",
          authtype: "jwt",
        },
      });

      if (axiosResponse?.status === 200 && axiosResponse?.data) {
        const blob = new Blob([axiosResponse.data], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `DA_Productivity_${viewType}_Report_${reportDate.format("YYYY-MM-DD")}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success("Export successful");
      } else {
        message.error("Failed to export data");
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error("Error exporting data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear existing data to prevent it from stalling on the new tab
    setData([]);
    setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
    
    // Reset page and filters when switching type/date
    setColumnFilters({});
    fetchReportData(1, pagination.pageSize, {}, viewType, reportDate);
  }, [viewType, reportDate]);

  return (
    <div className="da-productivity-report-container">
      <Spin spinning={loading}>
        <div className="report-header">
          <Heading title={tableConfig?.title || "DA Productivity Report"} />
        </div>

        <div className="main-content-layout">
          {/* 2. Filter Bar */}
          <div className="da-filter-bar">
            <div className="left-section">
              <span className="da-filter-label">REPORT DATE</span>
              <div className="date-picker-wrapper">
                <CalendarOutlined style={{ color: "#2E86DE", fontSize: 16 }} />
                <span className="date-text">
                  {reportDate.format("MMMM D, YYYY")}
                </span>
                <DownOutlined style={{ color: "#9E9E9E", fontSize: 14 }} />
                <DatePicker
                  value={reportDate}
                  onChange={(date) => setReportDate(date)}
                  allowClear={false}
                  disabledDate={(current) =>
                    current && current > moment().endOf("day")
                  }
                />
              </div>
            </div>
          </div>

          {/* 3. Report Type Tabs */}
          <div className="da-report-tabs">
            <div
              className="da-tab-card"
              data-active={viewType === "detailed" ? "true" : "false"}
              onClick={() => setViewType("detailed")}
            >
              <div className="tab-icon-wrap">
                <UnorderedListOutlined />
              </div>
              <div className="tab-content">
                <div className="tab-head">
                  <span className="tab-title">Detailed productivity</span>
                  {viewType === "detailed" && (
                    <span className="active-pill">
                      {pagination.total ? `Total: ${pagination.total}` : "Active"}
                    </span>
                  )}
                </div>
                <p className="tab-desc">
                  Line-by-line breakdown of each DA&apos;s performance and
                  shipment tracking
                </p>
              </div>
            </div>

            <div
              className="da-tab-card"
              data-active={viewType === "count" ? "true" : "false"}
              onClick={() => setViewType("count")}
            >
              <div className="tab-icon-wrap">
                <BarChartOutlined />
              </div>
              <div className="tab-content">
                <div className="tab-head">
                  <span className="tab-title">Count summary</span>
                  {viewType === "count" && (
                    <span className="active-pill">
                      {pagination.total ? `Total: ${pagination.total}` : "Active"}
                    </span>
                  )}
                </div>
                <p className="tab-desc">
                  Aggregated totals and delivery counts grouped by route or DA
                </p>
              </div>
            </div>
          </div>

          {/* 4. Content Card */}
          <div className="da-content-card">


            {/* 5. Actions Bar */}
            <div className="da-actions-bar">
              <div className="left-actions">
                {/* Search and Filters removed per user request */}
              </div>
              <div className="right-actions">
                <button
                  className="btn-refresh"
                  onClick={() =>
                    fetchReportData(
                      pagination.current,
                      pagination.pageSize,
                      columnFilters,
                      viewType,
                      reportDate,
                    )
                  }
                >
                  <ReloadOutlined />
                  Refresh
                </button>
                <button
                  className="btn-export"
                  onClick={handleExport}
                  title="Download full report as .csv"
                  disabled={!data || data.length === 0}
                >
                  <DownloadOutlined />
                  Download
                </button>
              </div>
            </div>

            {/* 7. Table OR 6. Empty State */}
            {data && data.length > 0 ? (
              <Table
                columns={columns}
                dataSource={data}
                pagination={{
                  ...pagination,
                  showTotal: (total) => `Total ${total} items`,
                }}
                onChange={handleTableChange}
                rowKey={(record) =>
                  record.id || record.awbNumber || Math.random()
                }
                scroll={{ x: "max-content" }}
                bordered={false}
                size="middle"
              />
            ) : (
              <div className="da-empty-state">
                <div className="icon-circle">
                  <TableOutlined />
                </div>
                <h3 className="empty-title">
                  No data found for {reportDate.format("MMMM D, YYYY")}
                </h3>
                <p className="empty-body">
                  There are no DA productivity records for this date. Try
                  selecting a different date using the date picker above.
                </p>
                <button
                  className="btn-try-date"
                  onClick={() => {
                    const pickerInput = document.querySelector(
                      ".date-picker-wrapper .ant-picker-input input",
                    );
                    if (pickerInput) pickerInput.click();
                  }}
                >
                  Try a different date
                </button>
              </div>
            )}
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default DAProductivityReport;
